import React, { useState, useCallback } from 'react';

import type { AuditTreeViewProps, AuditTreeNode } from './types';

const OPERATION_COLORS: Record<string, string> = {
  anonymization: 'var(--warning-bg, #fef3c7)',
  deletion: 'var(--danger-bg-subtle, #fee2e2)',
  redaction: 'var(--info-bg, #dbeafe)',
  resolution: 'var(--success-bg, #dcfce7)',
  generic: 'var(--bg-subtle, #f9fafb)',
};

const OPERATION_ICONS: Record<string, string> = {
  anonymization: '⊘',
  deletion: '✕',
  redaction: '▓',
  resolution: '✓',
  generic: '•',
};

interface TreeNodeProps {
  node: AuditTreeNode;
  depth: number;
  hasSensitive: boolean;
  sensitivePlaceholder: string;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
}

function TreeNode({
  node,
  depth,
  hasSensitive,
  sensitivePlaceholder,
  expandedIds,
  onToggle,
}: TreeNodeProps) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedIds.has(node.id);
  const isSensitiveBlocked = hasSensitive && node.sensitive;
  const opType = node.operationType ?? 'generic';

  const nodeStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: depth > 0 ? 'var(--space-5, 20px)' : 0,
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 'var(--space-2, 8px)',
    padding: 'var(--space-2, 8px) var(--space-3, 12px)',
    borderRadius: 'var(--radius-sm, 5px)',
    background: OPERATION_COLORS[opType],
    cursor: hasChildren ? 'pointer' : 'default',
    userSelect: 'none',
    marginBottom: 'var(--space-1, 4px)',
    position: 'relative',
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    flexShrink: 0,
    color: 'var(--fg-2, #374151)',
    marginTop: 1,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--text-sm, 12px)',
    fontWeight: 'var(--fw-medium, 500)',
    color: 'var(--fg-1, #111)',
    fontFamily: 'var(--font-mono)',
    flex: 1,
  };

  const metaStyle: React.CSSProperties = {
    fontSize: 'var(--text-xs, 11px)',
    color: 'var(--fg-3, #6b7280)',
    marginTop: 2,
    fontFamily: 'var(--font-mono)',
  };

  const sensitiveStyle: React.CSSProperties = {
    fontSize: 'var(--text-xs, 11px)',
    color: 'var(--fg-3, #6b7280)',
    fontStyle: 'italic',
    padding: 'var(--space-1, 4px) var(--space-2, 8px)',
    background: 'var(--bg-subtle, #f9fafb)',
    borderRadius: 'var(--radius-xs, 3px)',
    border: '1px dashed var(--border-default, #e5e7eb)',
    marginTop: 'var(--space-1, 4px)',
  };

  return (
    <div style={nodeStyle} data-testid={`audit-node-${node.id}`}>
      {/* Connector line */}
      {depth > 0 && (
        <div
          style={{
            position: 'absolute',
            left: depth * 20 - 12,
            top: 0,
            bottom: 0,
            width: 1,
            background: 'var(--border-default, #e5e7eb)',
          }}
        />
      )}

      <div
        role={hasChildren ? 'button' : undefined}
        tabIndex={hasChildren ? 0 : undefined}
        aria-expanded={hasChildren ? isExpanded : undefined}
        onClick={() => hasChildren && onToggle(node.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (hasChildren) onToggle(node.id);
          }
        }}
        style={rowStyle}
      >
        {/* Expand toggle */}
        {hasChildren && (
          <span
            style={{
              ...iconStyle,
              fontSize: 8,
              color: 'var(--fg-2, #374151)',
            }}
          >
            {isExpanded ? '▼' : '▶'}
          </span>
        )}

        {/* Operation icon */}
        <span style={iconStyle}>{OPERATION_ICONS[opType]}</span>

        {/* Content */}
        <div style={{ flex: 1 }}>
          {isSensitiveBlocked ? (
            <div style={sensitiveStyle} data-testid={`audit-node-${node.id}-sensitive`}>
              {sensitivePlaceholder}
            </div>
          ) : (
            <>
              <span style={labelStyle}>{node.label}</span>
              {(node.timestamp != null || node.actor != null) && (
                <div style={metaStyle}>
                  {node.timestamp && <span>{node.timestamp}</span>}
                  {node.actor && <span style={{ marginLeft: 8 }}>por {node.actor}</span>}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div role="group">
          {node.children!.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              hasSensitive={hasSensitive}
              sensitivePlaceholder={sensitivePlaceholder}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * AuditTreeView — cascade chain LGPD (ADR-107 cascade).
 *
 * Pattern DS v2.2.0 — primitivo universal para auditoria.
 * Usado em Brief 5 A5 origem D-B + B3 ConflictAlertCascadeTree + B5 Step 3 embed.
 *
 * hasSensitive=true: nodes com sensitive=true mostram placeholder neutro
 * em vez de detalhes (D-B defense-in-depth — capability audit-events-read-sensitive).
 *
 * Acessibilidade:
 * - role="tree" no container + role="treeitem" em cada nó
 * - aria-expanded em nós com filhos
 * - Keyboard: Enter/Space para expandir
 */
export function AuditTreeView({
  nodes,
  hasSensitive = false,
  sensitivePlaceholder = 'Detalhes restritos a perfis com acesso à auditoria sensível',
  expandedIds: initialExpandedIds = [],
  ariaLabel = 'Árvore de auditoria',
}: AuditTreeViewProps) {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set(initialExpandedIds));

  const handleToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  if (nodes.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--space-4, 16px)',
          color: 'var(--fg-3, #6b7280)',
          fontSize: 'var(--text-sm, 12px)',
          textAlign: 'center',
        }}
        data-testid="audit-tree-empty"
      >
        Nenhum evento de auditoria encontrado.
      </div>
    );
  }

  return (
    <div
      role="tree"
      aria-label={ariaLabel}
      style={{
        fontFamily: 'var(--font-sans, system-ui)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1, 4px)',
      }}
      data-testid="audit-tree"
    >
      {nodes.map((node) => (
        <TreeNode
          key={node.id}
          node={node}
          depth={0}
          hasSensitive={hasSensitive}
          sensitivePlaceholder={sensitivePlaceholder}
          expandedIds={expandedIds}
          onToggle={handleToggle}
        />
      ))}
    </div>
  );
}
